<div class="translations-wrapper" *ngIf="isLoaded">
  <!-- Only an explicit Hide row reaches this: an unconfigured role falls back to View. -->
  <div
    class="no-access"
    *ngIf="!isVisible"
    data-test-id="customer-item-translations-no-access"
  >
    Your role does not have access to item translations.
  </div>

  <ng-container *ngIf="isVisible">
    <div class="translations-header">
      <div class="search-box" *ngIf="rows.length">
        <input
          type="text"
          placeholder="Search item # or description…"
          data-test-id="customer-item-translations-search"
          [(ngModel)]="searchString"
          (input)="applySearch()"
        />
      </div>
    </div>

    <!-- Empty state: nothing translated yet for this customer. -->
    <div
      class="empty-state"
      *ngIf="!rows.length"
      data-test-id="customer-item-translations-empty"
    >
      <mat-icon>translate</mat-icon>
      <div class="empty-title">No translations yet</div>
      <div class="empty-hint">
        Translations are created when they are entered on this customer's
        orders (Stage 1 › Items).
      </div>
    </div>

    <div class="translations-table-card" *ngIf="rows.length">
      <table
        class="translations-table"
        data-test-id="customer-item-translations-table"
      >
        <thead>
          <tr>
            <th class="item-col">Item #</th>
            <th class="descr-col">Description (English)</th>
            <th *ngFor="let lang of languages">{{ lang.name }}</th>
          </tr>
        </thead>
        <tbody>
          <tr
            *ngFor="let row of pagedRows"
            [attr.data-test-id]="
              'customer-item-translations-row-' + row.itemExternalKey
            "
          >
            <td class="item-col num">{{ row.itemExternalKey }}</td>
            <td class="descr-col" [title]="row.itemDescription">
              {{ row.itemDescription || "—" }}
            </td>
            <td
              *ngFor="let lang of languages"
              class="lang-cell"
              [attr.data-test-id]="
                'customer-item-translations-cell-' +
                row.itemExternalKey +
                '-' +
                lang.tagKey
              "
            >
              <ng-container *ngIf="cellFor(row, lang) as translation">
                <span class="lang-text" [title]="translation.text">{{
                  translation.text
                }}</span>
                <span class="cell-actions" *ngIf="isEditable">
                  <button
                    class="tbl-action-btn edit"
                    type="button"
                    [attr.data-test-id]="
                      'customer-item-translations-edit-' +
                      row.itemExternalKey +
                      '-' +
                      lang.tagKey
                    "
                    (click)="openEditModal(row, lang)"
                  >
                    Edit
                  </button>
                  <button
                    class="tbl-action-btn delete"
                    type="button"
                    [attr.data-test-id]="
                      'customer-item-translations-delete-' +
                      row.itemExternalKey +
                      '-' +
                      lang.tagKey
                    "
                    (click)="openDeleteModal(row, lang)"
                  >
                    Delete
                  </button>
                </span>
              </ng-container>
              <span class="no-value" *ngIf="!cellFor(row, lang)">—</span>
            </td>
          </tr>
        </tbody>
      </table>
      <div
        class="no-results"
        *ngIf="!filteredRows.length"
        data-test-id="customer-item-translations-no-results"
      >
        No items match "{{ searchString }}".
      </div>
    </div>

    <app-paginator
      *ngIf="rows.length"
      [records]="paginationData"
      (pageSettings)="getPageSettings($event)"
    ></app-paginator>

    <!-- Foot-of-page banner: the grid is what the reader came for, so the explainer and
         the future-orders caveat follow it rather than pushing it down. -->
    <div
      class="translations-banner"
      data-test-id="customer-item-translations-banner"
    >
      <mat-icon>info</mat-icon>
      <p>
        Reusable translation library for this company's items. Values entered on
        orders are stored here and pre-fill future orders.
        <strong
          >Editing here affects future orders only — orders already submitted
          keep their text.</strong
        >
      </p>
    </div>
  </ng-container>
</div>

<!-- Edit modal -->
<div
  class="modal-el translation-modal"
  (click)="backdropEditClick($event)"
  *ngIf="isEditModal && selected"
>
  <div
    class="modal-info-box translation-info-box"
    role="dialog"
    aria-modal="true"
    data-test-id="customer-item-translations-edit-modal"
  >
    <div class="modal-header">
      <div class="modal-title">
        Edit {{ selected.language.name }} translation
      </div>
      <button class="close-btn" aria-label="Close" (click)="closeEditModal()">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="item-summary">
      <span class="item-number">Item {{ selected.row.itemExternalKey }}</span>
      <span class="item-descr" [title]="selected.row.itemDescription">{{
        selected.row.itemDescription
      }}</span>
    </div>

    <div class="modal-field">
      <label>{{ selected.language.name }} description</label>
      <textarea
        rows="3"
        data-test-id="customer-item-translations-edit-input"
        [(ngModel)]="editText"
      ></textarea>
    </div>

    <div class="future-note">
      <mat-icon>info</mat-icon>
      <span>
        This change pre-fills <strong>future orders</strong> using item
        {{ selected.row.itemExternalKey }}. Orders already submitted keep the
        text they were saved with.
      </span>
    </div>

    <div class="modal-actions form-actions">
      <button mat-stroked-button class="cancel-btn" (click)="closeEditModal()">
        Cancel
      </button>
      <button
        mat-button
        class="action-btn"
        data-test-id="customer-item-translations-edit-save"
        [disabled]="isEditSaveDisabled"
        (click)="saveEdit()"
      >
        Save
      </button>
    </div>
  </div>
</div>

<!-- Delete confirmation -->
<div
  class="modal-el translation-delete-modal"
  (click)="backdropDeleteClick($event)"
  *ngIf="isDeleteModal && selected"
>
  <div
    class="modal-info-box translation-delete-box"
    role="dialog"
    aria-modal="true"
    data-test-id="customer-item-translations-delete-modal"
  >
    <div class="confirm-header">
      <mat-icon class="warning-icon">warning_amber</mat-icon>
      <div class="confirm-text">
        <div class="confirm-title">Delete translation?</div>
        <div class="confirm-body">
          The <strong>{{ selected.language.name }}</strong> translation of item
          <strong>{{ selected.row.itemExternalKey }}</strong> ("{{
            selected.translation.text
          }}") will be removed from this customer's library.
        </div>
      </div>
    </div>
    <div class="confirm-note">
      Future orders will no longer pre-fill with it. Orders already submitted
      are not affected.
    </div>
    <div class="modal-actions form-actions">
      <button
        mat-stroked-button
        class="cancel-btn"
        (click)="closeDeleteModal()"
      >
        Cancel
      </button>
      <button
        mat-button
        class="delete-btn"
        data-test-id="customer-item-translations-delete-confirm"
        (click)="confirmDelete()"
      >
        Delete
      </button>
    </div>
  </div>
</div>
